import { Injectable } from '@angular/core';
import firebase from 'firebase/compat/app';
import { NGXLogger as LoggerService } from "ngx-logger";
import { BehaviorSubject, Observable } from 'rxjs';
import { SnackService } from './snack.service';

/** how long after the start the database connection may stay unconfirmed before `stalled$` turns true (report threshold) */
export const CONNECTION_STALL_MS = 10_000;
/** threshold of `stalledLong$` - the banner only shows from here on */
export const CONNECTION_BANNER_STALL_MS = 30_000;
/** threshold of `stalledVeryLong$` - the follow-up report "still no db connection after 60s" */
export const CONNECTION_VERY_LONG_STALL_MS = 60_000;

/**
 * why the current stall window started (workorder-app-check-connection-stalled-window-reset-2026-10-27, decision 2):
 * `start` = service construction, `online` = browser `online` event while not connected, `disconnect` = `.info/connected`
 * switched from true to false with the document visible, `visible` = the document became visible while not connected
 */
export type ConnectionWindowReason = 'start' | 'online' | 'disconnect' | 'visible';

/** the diagnostic values of the current window (see ConnectionStatusService class doc), all times performance.now() */
export interface ConnectionDiagnostics {
    /** start of the current (or last) window */
    startAtMs: number;
    /** why the current (or last) window started */
    windowReason: ConnectionWindowReason;
    /** sequence number of the current (or last) window, counts up with every window start */
    windowId: number;
    /** last switch of `.info/connected` to true; null while never connected */
    connectedAtMs: number | null;
    /** the window that was open at the last switch to true; null when none was open (or never connected) */
    connectedWindowId: number | null;
    /** the document was hidden at the construction or at any `visibilitychange` since */
    hiddenAny: boolean;
    /** the document is hidden now */
    hiddenNow: boolean;
}

@Injectable({
    providedIn: 'root'
})
/**
 * Mirrors the SDK's `.info/connected` flag. Read by HeaderService for the write diagnostics and
 * awaited once by PollService before the single retry of a denied first header write
 * (workorder-persist-new-poll-single-flight-2026-09-22, tasks 4 and 5).
 *
 * `stalled$` (workorder-app-check-connection-stalled-hint-2026-10-26, task 1): true when the connection is
 * still unconfirmed CONNECTION_STALL_MS into a stall window although the browser is online - the picture of a
 * database that rejects the client (App Check enforcement against a build without App Check: the server
 * closes the socket, the SDK never reconnects, nothing else fires) or of a proxy / firewall that blocks
 * firebaseio.com. Turns false as soon as `.info/connected` is true.
 *
 * Two more thresholds in the same window (workorder-app-check-connection-stalled-telemetry-2026-10-27, task 1):
 * `stalledLong$` (CONNECTION_BANNER_STALL_MS, drives the banner) and `stalledVeryLong$` (CONNECTION_VERY_LONG_STALL_MS,
 * the follow-up report). The ten-second report is ConnectionStalledReporter's job, not the banner's.
 *
 * Window model (workorder-app-check-connection-stalled-window-reset-2026-10-27, decision 1): a stall is N seconds in the
 * foreground without a confirmed connection since the start or since the last disconnect. A window = the three timers,
 * their elapsed flags and `startAtMs`. `startWindow(reason)` drops the previous one and starts anew: at the construction
 * (`start`), at `online` while not connected (`online`), at a switch of `.info/connected` from true to false with the
 * document visible and the browser online (`disconnect` - the SDK's first `false` at the start is no disconnect), and at
 * `visibilitychange` to visible while not connected (`visible`). `clearWindow()` drops timers and flags and sets the three
 * stall states to false: at `.info/connected === true`, at `offline` and at `visibilitychange` to hidden. While the
 * document is hidden nothing counts and no window starts (a page constructed in a hidden tab gets its first window when
 * it becomes visible). Before this model the elapsed flags stayed true for good, so every later `false` - typically the
 * short reconnect after a tab comes back from the background - raised all three stalls at once.
 *
 * Diagnostics (`diagnostics`): `startAtMs` is performance.now() at the start of the current window and the reference of
 * every duration in the ConnectionStalled reports, `windowReason` says which window it is. `connectedWindowId` tells the
 * reporter whether the connection came in the window of its ten-second report. `hiddenAny` counts since the construction
 * (not reset by a window).
 */
export class ConnectionStatusService {
    private _isConnected: boolean;
    /** last value the SDK reported; undefined until the first emission */
    private lastKnownConnected: boolean | undefined;
    private readonly connectedSubject = new BehaviorSubject<boolean | undefined>(undefined);
    private readonly stalledSubject = new BehaviorSubject<boolean>(false);
    private readonly stalledLongSubject = new BehaviorSubject<boolean>(false);
    private readonly stalledVeryLongSubject = new BehaviorSubject<boolean>(false);
    /** the stall timers of the current window */
    private stallTimers: Array<ReturnType<typeof setTimeout>> = [];
    /** per window: the threshold has elapsed */
    private stallElapsed = { stalled: false, long: false, veryLong: false };
    /** a window is running (started and neither connected nor cleared since) */
    private windowOpen = false;
    private windowId = 0;
    private windowReason: ConnectionWindowReason = 'start';
    private startAtMs = now();
    private connectedAtMs: number | null = null;
    private connectedWindowId: number | null = null;
    private hiddenAny = isDocumentHidden();

    constructor(
        private logger: LoggerService,
        private snackService: SnackService
    ) {
        this._isConnected = false;
        this.logger.log('ConnectionStatusService.constructor');
        this.setConnectionCallback();
        this.observeBrowserOnline();
        this.observeVisibility();
        if (!isDocumentHidden()) this.startWindow('start');
    }
    private setConnectionCallback() {
        const connectedRef = firebase.database().ref('.info/connected');
        connectedRef.on('value', (snap) => {
            const connected = snap.val() === true;
            const wasConnected = this.lastKnownConnected === true;
            if (connected && !wasConnected) {
                this.connectedAtMs = now();
                this.connectedWindowId = this.windowOpen ? this.windowId : null;
            }
            this.lastKnownConnected = connected;
            if (connected) this.clearWindow();
            else if (wasConnected && !isDocumentHidden() && this.isBrowserOnline()) this.startWindow('disconnect');
            if (connected) {
                this.logger.log('Connection to the server (firebase) established.');
                // this.uiMessage.warning("You are now connected to our server and you are ready to start your poker session.")
                this.snackService.closeOfflineMessage();
                this._isConnected = true;
            } else {
                const message = 'We are trying to connect to our server (https://whoo-dev.firebaseio.com / https://whoo-prod.firebaseio.com). Please check your internet connection or your firewall configuration.';
                //    this.snackService.openOfflineMessage(message)
                this.logger.log(message);
                this._isConnected = false;
            }
            this.evaluateStalled();
            this.connectedSubject.next(connected);
        });
    }
    public isConnected() {
        if (this._isConnected) {
            return true;
        } else {
            return false;
        }
    }

    /** the last `.info/connected` value the SDK reported - undefined before the first emission (diagnostics only) */
    /** ms since the last change of `.info/connected` to true while still connected, otherwise null - diagnostics only */
    public get msSinceConnected(): number | null {
        return this.lastKnownConnected === true && this.connectedAtMs !== null ? Math.round(now() - this.connectedAtMs) : null;
    }

    public get connected(): boolean | undefined {
        return this.lastKnownConnected;
    }

    /** every `.info/connected` value the SDK reports (undefined before the first one) */
    public get connected$(): Observable<boolean | undefined> {
        return this.connectedSubject.asObservable();
    }

    /** true while the connection is unconfirmed for longer than CONNECTION_STALL_MS with the browser online (see class doc) */
    public get stalled$(): Observable<boolean> {
        return this.stalledSubject.asObservable();
    }

    /** current value of `stalled$` */
    public get stalled(): boolean {
        return this.stalledSubject.value;
    }

    /** like `stalled$` with CONNECTION_BANNER_STALL_MS */
    public get stalledLong$(): Observable<boolean> {
        return this.stalledLongSubject.asObservable();
    }

    /** like `stalled$` with CONNECTION_VERY_LONG_STALL_MS */
    public get stalledVeryLong$(): Observable<boolean> {
        return this.stalledVeryLongSubject.asObservable();
    }

    /** the diagnostic values of the current window (see class doc) */
    public get diagnostics(): ConnectionDiagnostics {
        return {
            startAtMs: this.startAtMs,
            windowReason: this.windowReason,
            windowId: this.windowId,
            connectedAtMs: this.connectedAtMs,
            connectedWindowId: this.connectedWindowId,
            hiddenAny: this.hiddenAny,
            hiddenNow: isDocumentHidden(),
        };
    }

    /**
     * Resolves as soon as the SDK reports the connection as established, or after `timeoutMs`
     * (never rejects: the caller retries its write either way, the log tells the two apart).
     */
    public waitForConnected(timeoutMs: number): Promise<boolean> {
        if (this.lastKnownConnected === true) return Promise.resolve(true);
        return new Promise<boolean>(resolve => {
            const ref = firebase.database().ref('.info/connected');
            const timer = setTimeout(() => { ref.off('value', onValue); resolve(false); }, timeoutMs);
            const onValue = (snap: firebase.database.DataSnapshot) => {
                if (snap.val() !== true) return;
                clearTimeout(timer);
                ref.off('value', onValue);
                resolve(true);
            };
            ref.on('value', onValue);
        });
    }

    // ------------------------------------------------------------------ stall detection
    /** drops the previous window and starts a new one (see class doc) */
    private startWindow(reason: ConnectionWindowReason): void {
        this.clearWindow();
        this.windowOpen = true;
        this.windowId++;
        this.windowReason = reason;
        this.startAtMs = now();
        const windows: Array<[keyof ConnectionStatusService['stallElapsed'], number]> = [
            ['stalled', CONNECTION_STALL_MS],
            ['long', CONNECTION_BANNER_STALL_MS],
            ['veryLong', CONNECTION_VERY_LONG_STALL_MS],
        ];
        this.stallTimers = windows.map(([key, ms]) => setTimeout(() => {
            this.stallElapsed[key] = true;
            this.evaluateStalled();
        }, ms));
    }

    /** drops timers and elapsed flags and sets the three stall states to false; `startAtMs` and the reason stay */
    private clearWindow(): void {
        this.stallTimers.forEach(timer => clearTimeout(timer));
        this.stallTimers = [];
        this.stallElapsed = { stalled: false, long: false, veryLong: false };
        this.windowOpen = false;
        this.evaluateStalled();
    }

    private evaluateStalled(): void {
        const unconfirmed = this.lastKnownConnected !== true && this.isBrowserOnline();
        const stalled = this.stallElapsed.stalled && unconfirmed;
        if (stalled !== this.stalledSubject.value) {
            if (stalled) this.logger.warn(`ConnectionStatusService: no database connection ${CONNECTION_STALL_MS / 1000}s after the window start (${this.windowReason}) while the browser is online`);
            this.stalledSubject.next(stalled);
        }
        const stalledLong = this.stallElapsed.long && unconfirmed;
        if (stalledLong !== this.stalledLongSubject.value) this.stalledLongSubject.next(stalledLong);
        const stalledVeryLong = this.stallElapsed.veryLong && unconfirmed;
        if (stalledVeryLong !== this.stalledVeryLongSubject.value) this.stalledVeryLongSubject.next(stalledVeryLong);
    }

    private observeBrowserOnline(): void {
        if (typeof window === 'undefined') return;
        window.addEventListener('offline', () => {
            // the offline message of the app covers this case; a stall only counts with network
            this.clearWindow();
        });
        window.addEventListener('online', () => {
            // back online: the SDK reconnects on its own, give it the full windows again
            if (this.lastKnownConnected !== true && !isDocumentHidden()) this.startWindow('online');
        });
    }

    private observeVisibility(): void {
        if (typeof document === 'undefined') return;
        document.addEventListener('visibilitychange', () => {
            if (isDocumentHidden()) {
                this.hiddenAny = true;
                // in the background the OS may cut the socket at will - nothing counts there
                this.clearWindow();
            } else if (this.lastKnownConnected !== true) {
                this.startWindow('visible');
            }
        });
    }

    private isBrowserOnline(): boolean {
        return typeof navigator === 'undefined' || navigator.onLine !== false;
    }
}

function now(): number {
    return typeof performance === 'undefined' ? Date.now() : performance.now();
}

function isDocumentHidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}
